import { create } from 'zustand';
import type { Client } from '@microsoft/microsoft-graph-client';
import { readJson, writeJson } from '@/lib/graph/onedrive-helpers';

// ============================================================================
// TYPES
// ============================================================================

export type OnboardingState = 
  | 'NEW'
  | 'CONSENT_REQUIRED'
  | 'CONSENT_REQUESTED'
  | 'CONSENT_GRANTED'
  | 'TEMPLATE_SELECTED'
  | 'FOCUS_SELECTED'
  | 'SNAPSHOT_REQUESTED'
  | 'SNAPSHOT_RUNNING'
  | 'SNAPSHOT_DELIVERED'
  | 'SCHEDULE_PROMPTED'
  | 'SCHEDULE_SET'
  | 'CONVERSATIONS_PROMPTED'
  | 'CONVERSATIONS_SET'
  | 'WEEKLY_DIGEST_1_DELIVERED'
  | 'WEEKLY_DIGEST_2_DELIVERED'
  | 'WEEK_OVER_WEEK_DELIVERED'
  | 'MONTH_TO_DATE_DELIVERED'
  | 'REVIEW_PACKET_DELIVERED'
  | 'TRIAL_EXPIRED'
  | 'PAID_ACTIVE';

export interface OnboardingUserState {
  tenantId: string;
  userId: string;
  state: OnboardingState;
  trialStartAt: string;
  trialEndsAt: string;
  consentGrantedAt?: string;
  firstSnapshotAt?: string;
  scheduleSetAt?: string;
  lastEngagedAt: string;
  nextRunAt?: string;
  cadence?: 'weekly' | 'biweekly' | 'monthly';
  weeklyDigestCount: number;
  outputsCount: number;
  shareClicksCount: number;
  selectedTemplate?: string;
  selectedFocus?: string;
  projectDetails?: { name: string; keywords?: string[] };
  notificationsPaused: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ChecklistItem {
  id: string;
  label: string;
  completed: boolean;
  route?: string;
  description: string;
}

// ============================================================================
// STORE
// ============================================================================

const ONBOARDING_PATH = 'config/onboarding-state.json';

interface OnboardingStore {
  state: OnboardingUserState | null;
  checklist: ChecklistItem[];
  isLoading: boolean;
  isRunningNow: boolean;
  error: string | null;
  
  loadOnboardingState: (graph: Client, userId: string) => Promise<void>;
  runNow: (graph: Client, userId: string) => Promise<void>;
  getTrialDaysRemaining: () => number;
  getTrialDay: () => number;
}

function getDefaultState(userId: string): OnboardingUserState {
  const now = new Date().toISOString();
  const trialEnd = new Date();
  trialEnd.setDate(trialEnd.getDate() + 30);
  
  return {
    tenantId: 'unknown',
    userId,
    state: 'CONSENT_GRANTED', // Assume consent is granted if user can access companion app
    trialStartAt: now,
    trialEndsAt: trialEnd.toISOString(),
    lastEngagedAt: now,
    weeklyDigestCount: 0,
    outputsCount: 0,
    shareClicksCount: 0,
    notificationsPaused: false,
    createdAt: now,
    updatedAt: now,
  };
}

function buildChecklist(state: OnboardingUserState | null): ChecklistItem[] {
  if (!state) {
    return [
      { id: 'consent', label: 'Sign in / Consent', completed: false, description: 'Grant permissions to access your work data' },
      { id: 'schedule', label: 'Set Schedule', completed: false, route: '/schedule', description: 'Configure proactive check-ins' },
      { id: 'keywords', label: 'Add Keywords/Projects', completed: false, route: '/keywords', description: 'Help the agent find relevant work' },
      { id: 'conversations', label: 'Choose Conversations', completed: false, route: '/conversations', description: 'Select Teams channels to monitor' },
    ];
  }
  
  const stateOrder = [
    'NEW', 'CONSENT_REQUIRED', 'CONSENT_REQUESTED', 'CONSENT_GRANTED',
    'TEMPLATE_SELECTED', 'FOCUS_SELECTED', 'SNAPSHOT_REQUESTED', 'SNAPSHOT_RUNNING',
    'SNAPSHOT_DELIVERED', 'SCHEDULE_PROMPTED', 'SCHEDULE_SET', 'CONVERSATIONS_PROMPTED',
    'CONVERSATIONS_SET',
  ];
  
  const currentIndex = stateOrder.indexOf(state.state);
  
  return [
    {
      id: 'consent',
      label: 'Sign in / Consent',
      completed: !!state.consentGrantedAt || currentIndex >= stateOrder.indexOf('CONSENT_GRANTED'),
      description: 'Grant permissions to access your work data',
    },
    {
      id: 'schedule',
      label: 'Set Schedule',
      completed: !!state.scheduleSetAt || currentIndex >= stateOrder.indexOf('SCHEDULE_SET'),
      route: '/schedule',
      description: 'Configure proactive check-ins',
    },
    {
      id: 'keywords',
      label: 'Add Keywords/Projects',
      completed: state.outputsCount > 0 && (state.selectedFocus === 'project' || state.selectedFocus === 'keyword'),
      route: '/keywords',
      description: 'Help the agent find relevant work',
    },
    {
      id: 'conversations',
      label: 'Choose Conversations',
      completed: state.state === 'CONVERSATIONS_SET' || currentIndex > stateOrder.indexOf('CONVERSATIONS_SET'),
      route: '/conversations',
      description: 'Select Teams channels to monitor',
    },
  ];
}

export const useOnboardingStore = create<OnboardingStore>((set, get) => ({
  state: null,
  checklist: buildChecklist(null),
  isLoading: false,
  isRunningNow: false,
  error: null,
  
  loadOnboardingState: async (graph: Client, userId: string) => {
    set({ isLoading: true, error: null });
    
    try {
      let state = await readJson<OnboardingUserState>(graph, ONBOARDING_PATH);
      
      if (!state) {
        state = getDefaultState(userId);
        await writeJson(graph, ONBOARDING_PATH, state);
      }
      
      set({
        state,
        checklist: buildChecklist(state),
        isLoading: false,
      });
    } catch (error) {
      console.error('[OnboardingStore] Error loading state:', error);
      
      // Use default state on error
      const defaultState = getDefaultState(userId);
      set({
        state: defaultState,
        checklist: buildChecklist(defaultState),
        isLoading: false,
        error: 'Failed to load onboarding state',
      });
    }
  },
  
  runNow: async (graph: Client, userId: string) => {
    set({ isRunningNow: true, error: null });
    
    try {
      // Call the Run Now API endpoint
      const functionsUrl = import.meta.env.VITE_FUNCTIONS_URL || 'http://localhost:7071';
      const response = await fetch(`${functionsUrl}/api/TriggerProactive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          trigger: 'run_now',
        }),
      });
      
      if (!response.ok) {
        throw new Error('Failed to trigger snapshot');
      }
      
      // Update local state
      const state = get().state;
      if (state) {
        const updatedState = {
          ...state,
          lastEngagedAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        
        await writeJson(graph, ONBOARDING_PATH, updatedState);
        set({
          state: updatedState,
          checklist: buildChecklist(updatedState),
        });
      }
      
      set({ isRunningNow: false });
    } catch (error) {
      console.error('[OnboardingStore] Run now error:', error);
      set({ isRunningNow: false, error: 'Failed to run snapshot' });
    }
  },
  
  getTrialDaysRemaining: () => {
    const state = get().state;
    if (!state) return 30;
    
    const end = new Date(state.trialEndsAt);
    const now = new Date();
    const diffMs = end.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
  },
  
  getTrialDay: () => {
    const state = get().state;
    if (!state) return 0;
    
    const start = new Date(state.trialStartAt);
    const now = new Date();
    const diffMs = now.getTime() - start.getTime();
    return Math.floor(diffMs / (1000 * 60 * 60 * 24));
  },
}));
